import React, { useState } from 'react';
import { CheckCircle, Send } from 'lucide-react';
import { submitContact } from '../../lib/api';
import { withTimeout } from '../../lib/utils';
import { Input } from '../ui/input';

// Backends can take a while to wake up from a cold start — don't make the
// visitor wait more than this for a response before showing success anyway.
const SUBMIT_TIMEOUT_MS = 4000;

export default function ProgramLeadForm({ program, onSuccess }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const contactPromise = submitContact({
      ...form,
      course: program.title,
      message: `Landing page enquiry for ${program.title}`,
      source: 'program_landing_page',
      page: `program_landing_page:${program.slug}`,
    });

    const result = await withTimeout(contactPromise, SUBMIT_TIMEOUT_MS);
    setLoading(false);
    if (!result.timedOut && result.error) {
      setError('Something went wrong. Please try again.');
      return;
    }
    setSent(true);
    // Brief, fixed pause to show the confirmation — independent of how long
    // the network call actually took.
    setTimeout(() => onSuccess?.(form), 600);
  };

  if (sent) {
    return (
      <div
        data-testid="lead-form-success"
        className="flex flex-col items-center justify-center text-center py-12 px-6 bg-white rounded-2xl border border-[#E1E7EF] shadow-sm"
      >
        <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: program.bg }}>
          <CheckCircle className="w-7 h-7" style={{ color: program.accent }} />
        </div>
        <h3 className="font-heading font-bold text-xl text-[#1F1F1F]">Thank You!</h3>
        <p className="font-body text-sm text-[#585858] mt-2">
          We've received your details for <strong>{program.title}</strong>. Our team will call you shortly.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="program-lead-form"
      className="bg-white rounded-2xl border border-[#E1E7EF] shadow-sm p-6 lg:p-7"
    >
      <h3 className="font-heading font-bold text-lg text-[#1F1F1F] mb-1">Get Course Details</h3>
      <p className="font-body text-sm text-[#585858] mb-5">Share your details and our counsellor will reach out to you.</p>

      <div className="space-y-4">
        <div>
          <label className="font-body text-sm font-medium text-[#1F1F1F] mb-1.5 block">Full Name *</label>
          <Input
            data-testid="lead-name-input"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            required
            placeholder="Your full name"
            className="h-11 border-[#E1E7EF] focus:border-[#0056D2] rounded-md"
          />
        </div>
        <div>
          <label className="font-body text-sm font-medium text-[#1F1F1F] mb-1.5 block">Email *</label>
          <Input
            data-testid="lead-email-input"
            type="email"
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            required
            placeholder="your@email.com"
            className="h-11 border-[#E1E7EF] focus:border-[#0056D2] rounded-md"
          />
        </div>
        <div>
          <label className="font-body text-sm font-medium text-[#1F1F1F] mb-1.5 block">Phone Number *</label>
          <Input
            data-testid="lead-phone-input"
            type="tel"
            value={form.phone}
            onChange={e => setForm({ ...form, phone: e.target.value })}
            required
            placeholder="+91 XXXXX XXXXX"
            className="h-11 border-[#E1E7EF] focus:border-[#0056D2] rounded-md"
          />
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}

        <button
          type="submit"
          disabled={loading}
          data-testid="lead-submit-btn"
          className="w-full font-body font-semibold text-sm text-white py-3 rounded-md transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
          style={{ backgroundColor: program.accent }}
        >
          <Send className="w-4 h-4" />
          {loading ? 'Submitting...' : 'Request Callback'}
        </button>
        <p className="text-[11px] text-center text-[#9AA5B5]">By submitting, you agree to be contacted about this course.</p>
      </div>
    </form>
  );
}
